import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PopularLazyLoadSelector, sortBy } from 'webapp/utils/common';

import makeArray from 'webapp/helpers/make-array';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import CommonAddButtonComponent from 'webapp/components/common/add-button';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import TeamsIndexGridItemComponent from 'webapp/components/teams/index/grid-item';

import FilterSortTeamInGrid from 'webapp/models-custom/filter-sort/team-in-grid';
import type Team from 'webapp/models-custom/team';

type Args = Record<string, never>;

export default class TeamsIndexGridComponent extends Component {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;

	declare filterSort: FilterSortTeamInGrid;

	addButtonText = 'New Space';
	visibleCheckContainerNode: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortTeamInGrid({
			selectors: PopularLazyLoadSelector.grid,

			getAllItems: () => {
				return this.sortedModel;
			},

			afterFilter: () => {
				const node = this.visibleCheckContainerNode;
				if (node) {
					this.triggerContentChange(node);
				}
			},
		});
	}

	get addButtonContainerNode(): HTMLElement | null {
		return this.navigationService.addButtonContainerNode;
	}

	get model(): Team[] {
		return this.mmTeamsService.userTeamModels;
	}

	get sortedModel(): Team[] {
		return sortBy(this.model, 'order', 'numericId');
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}

	@action
	createTeam(): void {
		this.mmTeamsService.createAndOpenEditTeamPopup();
	}
	<template>
		<FilterLineProxyComponent @model={{this.filterSort}} />

		<FilterNoResultsComponent @model={{this.filterSort}} />

		<CommonListLazyLoadComponent
			@classNames="lazy-grid"
			@filterSort={{this.filterSort}}
			@initialRowCount={{10}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@autoLoaded={{true}}
			@bottomDistance={{700}}
			as |visibleItems exporter|
		>

			<ul
				class="flex-ct flex-ct--size-team"
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |team|}}
					<TeamsIndexGridItemComponent @model={{team}} />
				{{/each}}

				{{#if exporter.areAllItemsLoaded}}
					<li
						role="button"
						class="card card--placeholder-new flex-item flex-item--placeholder filter-all"
						{{on "click" this.createTeam}}
					>
						<div class="default">
							<CommonUiIconComponent @name="Plus" @size="xl" />
							New Space
						</div>
					</li>
				{{/if}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=exporter.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>

			{{#unless exporter.areAllItemsLoaded}}
				<CommonListLoaderComponent />
			{{/unless}}

		</CommonListLazyLoadComponent>

		{{#if this.addButtonContainerNode}}
			{{#in-element this.addButtonContainerNode}}
				<CommonAddButtonComponent
					@text={{this.addButtonText}}
					@callTrigger={{this.createTeam}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
